import type { HttpClient } from '~/api/client';
import { httpClient } from '~/api/client';
import type {
  CancellableProductOrder,
  ClaimDetail,
  ClaimHistoryPage,
  ClaimReasonItem,
  OrderClaim,
  OrderProductItem,
} from '~/api/model';

// Response 타입 (Zod Model 재사용 — OAISchema 금지, api-typegen §0)
type CancellableProductOrderResponse = CancellableProductOrder;
type OrderClaimResponse = OrderClaim;
type ProductOrderResponse = OrderProductItem;
type ClaimReasonResponse = ClaimReasonItem;
type ClaimDetailResponse = ClaimDetail;
export type ClaimHistoryPageResponse = ClaimHistoryPage;

// CreateClaim 응답 DTO 직접 정의 (OAISchema 금지 — §0)
type CreateClaimResponse = {
  claimId?: number;
};

// Request DTO 직접 정의 (OAISchema 금지 — §0)
export type CancelProductOrdersRequestBody = {
  detailReason?: string;
  productOrderIds: number[];
  reason:
    | 'NONE'
    | 'CHANGE_OF_MIND'
    | 'WRONG_ORDER'
    | 'DELAY_IN_DELIVERY'
    | 'FAULTY_PRODUCT'
    | 'WRONG_PRODUCT'
    | 'NON_ARRIVAL'
    | 'CANCEL_ADMIN';
};

export interface ClaimDataSource {
  getClaims(params?: {
    lastOfferId?: number;
    size?: number;
  }): Promise<ClaimHistoryPageResponse>;
  getCancellableProductOrders(
    orderId: number,
  ): Promise<CancellableProductOrderResponse>;
  getCanceledProductDetail(productOrderId: number): Promise<OrderClaimResponse>;
  cancelProductOrders(data: CancelProductOrdersRequestBody): Promise<void>;
  getClaimableProductOrders(
    vendorOrderId: number,
  ): Promise<ProductOrderResponse[]>;
  getClaimReasons(): Promise<ClaimReasonResponse[]>;
  getClaimDetail(claimId: number): Promise<ClaimDetailResponse>;
  createClaim(formData: FormData): Promise<CreateClaimResponse>;
}

export class ClaimDataSourceImpl implements ClaimDataSource {
  // eslint-disable-next-line no-useless-constructor
  constructor(private readonly client: HttpClient = httpClient) {}

  async getClaims(params?: { lastOfferId?: number; size?: number }) {
    return this.client.request<ClaimHistoryPageResponse>({
      url: '/api/market-place/v1/claim',
      method: 'get',
      params,
    });
  }

  async getCancellableProductOrders(orderId: number) {
    return this.client.request<CancellableProductOrderResponse>({
      url: `/api/market-place/v2/orders/${orderId}/cancellable-product-orders`,
      method: 'get',
    });
  }

  async getCanceledProductDetail(productOrderId: number) {
    return this.client.request<OrderClaimResponse>({
      url: `/api/market-place/v1/claim/cancellation/${productOrderId}/detail`,
      method: 'get',
    });
  }

  async cancelProductOrders(data: CancelProductOrdersRequestBody) {
    await this.client.request<unknown>({
      url: '/api/market-place/v2/claim/kpn/cancel-product-orders',
      method: 'post',
      data,
    });
  }

  async getClaimableProductOrders(vendorOrderId: number) {
    return this.client.request<ProductOrderResponse[]>({
      url: `/api/market-place/v1/orders/${vendorOrderId}/claimable-product-orders`,
      method: 'get',
    });
  }

  async getClaimReasons() {
    return this.client.request<ClaimReasonResponse[]>({
      url: '/api/market-place/v1/claim/reason',
      method: 'get',
    });
  }

  async getClaimDetail(claimId: number) {
    return this.client.request<ClaimDetailResponse>({
      url: `/api/market-place/v1/claim/${claimId}/detail`,
      method: 'get',
    });
  }

  async createClaim(formData: FormData) {
    return this.client.request<CreateClaimResponse>({
      url: '/api/market-place/v1/claim',
      method: 'post',
      data: formData,
    });
  }
}

export const claimDataSource = new ClaimDataSourceImpl();
